/**
 * RELLENAR LA PLANTILLA DE LA PERSONA (28-09-2026).
 *
 * «Pónmelo en este documento»: la persona sube SU Excel o SU Word (el de la
 * cooperativa, la certificadora, el suyo) y el asistente decide qué va en cada
 * columna (herramienta `document_fill`). Aquí se escribe eso en el ORIGINAL y se
 * devuelve la MISMA plantilla: se edita el XML de dentro del fichero (un .xlsx y
 * un .docx son un zip de XML), así que se conservan formatos, colores, logos,
 * celdas combinadas, anchos y todo lo que no se toca. SheetJS reescribiendo el
 * libro perdería los estilos.
 *
 * Excel:
 *   · cada valor en su celda (fila y columna ABSOLUTAS: `origen` + índice leído);
 *   · la celda nueva hereda el estilo de la de arriba (bordes, fuente);
 *   · texto como «inlineStr» (sin tocar sharedStrings), número como número y fecha
 *     como fecha de Excel CON formato de fecha (si la celda no lo tenía, se clona su
 *     estilo con el formato dd/mm/aaaa);
 *   · si la plantilla es una Tabla de Excel, la tabla se estira hasta la última fila;
 *   · si se pisa una fórmula, se quita calcChain.xml (Excel lo rehace solo).
 * Word:
 *   · la tabla elegida: sus filas se rellenan y, si faltan, se clona la primera fila
 *     de datos (mismo formato);
 *   · las líneas «Titular: ______» se completan con el valor.
 * CSV: se reescribe con el mismo separador.
 *
 * PURO salvo DOMParser/XMLSerializer (navegador; jsdom en los tests).
 */

import type { Celda } from "./leer";

export interface RellenoTabla {
  /** Nombre de la hoja (Excel/CSV) o «Tabla N» (Word), como la leyó leer.ts. */
  hoja: string;
  /** Primera fila (índice de `celdas`, base 0) donde se escriben datos. */
  primeraFila: number;
  /** Para cada valor de una fila, su columna (índice de `celdas`, base 0). */
  columnas: number[];
  filas: Celda[][];
  /** «reemplazar»: se vacían antes las filas de datos que ya había en esas columnas. */
  modo: "reemplazar" | "anadir";
  /** Filas de datos que ya tenía la plantilla a partir de `primeraFila` (se vacían si «reemplazar»). */
  filasExistentes?: number;
}

export interface RellenoCampo {
  /** Excel: la celda (índices de `celdas`) donde va el valor. */
  hoja?: string;
  fila?: number;
  columna?: number;
  /** Word: el párrafo (índice entre los párrafos del cuerpo) «Etiqueta: ____». */
  parrafo?: number;
  etiqueta?: string;
  valor: string;
}

export interface Relleno {
  tablas: RellenoTabla[];
  campos: RellenoCampo[];
}

/** Lo que hace falta saber del documento leído para escribir en su sitio. */
export interface HojaDestino {
  nombre: string;
  origen?: { fila: number; columna: number };
  indice?: number;
}

/* ── Utilidades de celdas de Excel ───────────────────────────────────────── */

export function letraColumna(c: number): string {
  let s = "";
  let n = c + 1;
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

export function columnaDeLetra(l: string): number {
  let n = 0;
  for (const ch of l.toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

export function refCelda(fila: number, col: number): string {
  return `${letraColumna(col)}${fila + 1}`;
}

export function partirRef(ref: string): { fila: number; col: number } {
  const m = /^([A-Z]+)(\d+)$/i.exec(ref.replace(/\$/g, ""));
  if (!m) return { fila: 0, col: 0 };
  return { fila: Number(m[2]) - 1, col: columnaDeLetra(m[1]) };
}

/** ISO «2026-09-28» (o con hora) → número de serie de Excel (sistema 1900). */
export function serialExcel(iso: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?: (\d{2}):(\d{2}))?$/.exec(iso);
  if (!m) return null;
  const dias = (Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) - Date.UTC(1899, 11, 30)) / 86_400_000;
  const hora = m[4] != null ? (Number(m[4]) * 60 + Number(m[5])) / 1440 : 0;
  return dias + hora;
}

const esFechaIso = (v: unknown): v is string => typeof v === "string" && /^\d{4}-\d{2}-\d{2}( \d{2}:\d{2})?$/.test(v);

/** «2026-09-28» → «28/09/2026» (para Word, CSV y celdas sin formato de fecha posible). */
export function fechaEspanola(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?: (\d{2}:\d{2}))?$/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}${m[4] ? ` ${m[4]}` : ""}` : iso;
}

/** Texto para Word/CSV: fechas a la española, números con coma decimal. */
export function textoDeCelda(v: Celda): string {
  if (v == null) return "";
  if (typeof v === "number") return String(v).replace(".", ",");
  return esFechaIso(v) ? fechaEspanola(v) : v;
}

/* ── Zip ─────────────────────────────────────────────────────────────────── */

type Zip = Record<string, Uint8Array>;

async function abrirZip(bytes: Uint8Array): Promise<Zip> {
  const { unzipSync } = await import("fflate");
  return unzipSync(bytes);
}

async function cerrarZip(zip: Zip): Promise<Uint8Array> {
  const { zipSync } = await import("fflate");
  return zipSync(zip, { level: 6 });
}

const dec = (u: Uint8Array) => new TextDecoder().decode(u);
// Copia a un Uint8Array de ESTE contexto: fflate comprueba `instanceof Uint8Array`, y un
// TextEncoder de otro contexto (jsdom, un iframe) daría un zip roto.
const enc = (s: string) => new Uint8Array(new TextEncoder().encode(s));
const xmlDe = (u: Uint8Array) => new DOMParser().parseFromString(dec(u), "application/xml");
const aXml = (d: Document) => {
  const s = new XMLSerializer().serializeToString(d);
  return enc(s.startsWith("<?xml") ? s : `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n${s}`);
};

const SS = "http://schemas.openxmlformats.org/spreadsheetml/2006/main";
const REL = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
const PKG_REL = "http://schemas.openxmlformats.org/package/2006/relationships";

const XML_NS = "http://www.w3.org/XML/1998/namespace";
/** `xml:space="preserve"` bien puesto (con su espacio de nombres): conserva los espacios del texto. */
const preservar = (t: Element) => t.setAttributeNS(XML_NS, "xml:space", "preserve");

const hijos = (el: Element, local: string) => Array.from(el.children).filter((x) => x.localName === local);

/** Ruta de dentro del zip relativa a una carpeta («worksheets/sheet1.xml» desde xl/ → xl/worksheets/sheet1.xml). */
function resolverRuta(base: string, target: string): string {
  if (target.startsWith("/")) return target.slice(1);
  const partes = base.split("/").filter(Boolean);
  for (const p of target.split("/")) {
    if (p === "..") partes.pop();
    else if (p !== ".") partes.push(p);
  }
  return partes.join("/");
}

/* ── Excel ───────────────────────────────────────────────────────────────── */

/** Los formatos de número que son fecha (los de serie de Excel y los propios con d/m/y). */
function esFormatoFecha(id: number, codigo: string | null): boolean {
  if ((id >= 14 && id <= 22) || (id >= 45 && id <= 47) || (id >= 27 && id <= 36) || (id >= 50 && id <= 58)) return true;
  if (!codigo) return false;
  const limpio = codigo.replace(/"[^"]*"/g, "").replace(/\[[^\]]*\]/g, "").replace(/\\./g, "");
  return /[dy]/i.test(limpio) || /m{1,4}[^0#]*[dy]/i.test(limpio);
}

interface Estilos {
  doc: Document;
  xfs: Element;
  esFecha: (s: number) => boolean;
  /** El estilo `s` con formato de fecha (clonado si hace falta). */
  conFecha: (s: number) => number;
  cambiado: boolean;
}

function leerEstilos(zip: Zip): Estilos | null {
  const u = zip["xl/styles.xml"];
  if (!u) return null;
  const doc = xmlDe(u);
  const xfs = doc.getElementsByTagNameNS(SS, "cellXfs")[0];
  if (!xfs) return null;
  const codigos = new Map<number, string>();
  for (const nf of Array.from(doc.getElementsByTagNameNS(SS, "numFmt"))) {
    codigos.set(Number(nf.getAttribute("numFmtId")), nf.getAttribute("formatCode") ?? "");
  }
  const lista = () => hijos(xfs, "xf");
  const esFecha = (s: number) => {
    const xf = lista()[s];
    if (!xf) return false;
    const id = Number(xf.getAttribute("numFmtId") ?? "0");
    return esFormatoFecha(id, codigos.get(id) ?? null);
  };
  const clones = new Map<number, number>();
  const est: Estilos = {
    doc,
    xfs,
    esFecha,
    cambiado: false,
    conFecha: (s: number) => {
      if (esFecha(s)) return s;
      const hecho = clones.get(s);
      if (hecho != null) return hecho;
      const base = lista()[s] ?? lista()[0];
      const nuevo = (base ? base.cloneNode(true) : doc.createElementNS(SS, "xf")) as Element;
      nuevo.setAttribute("numFmtId", "14");
      nuevo.setAttribute("applyNumberFormat", "1");
      xfs.appendChild(nuevo);
      const idx = lista().length - 1;
      xfs.setAttribute("count", String(lista().length));
      clones.set(s, idx);
      est.cambiado = true;
      return idx;
    },
  };
  return est;
}

/** Hoja por nombre → su ruta en el zip. */
function rutaDeHoja(zip: Zip, nombre: string): string | null {
  const wb = zip["xl/workbook.xml"];
  const rels = zip["xl/_rels/workbook.xml.rels"];
  if (!wb || !rels) return null;
  const dwb = xmlDe(wb);
  const hojas = Array.from(dwb.getElementsByTagNameNS(SS, "sheet"));
  const hoja = hojas.find((h) => h.getAttribute("name") === nombre) ?? (hojas.length === 1 ? hojas[0] : undefined);
  if (!hoja) return null;
  const rid = hoja.getAttributeNS(REL, "id") ?? hoja.getAttribute("r:id");
  const drel = xmlDe(rels);
  const rel = Array.from(drel.getElementsByTagNameNS(PKG_REL, "Relationship")).find((r) => r.getAttribute("Id") === rid);
  const target = rel?.getAttribute("Target");
  return target ? resolverRuta("xl", target) : null;
}

interface HojaXml {
  doc: Document;
  sheetData: Element;
  /** Fila (base 0) → su <row>. */
  filas: Map<number, Element>;
  quitoFormulas: boolean;
}

function abrirHoja(u: Uint8Array): HojaXml | null {
  const doc = xmlDe(u);
  const sheetData = doc.getElementsByTagNameNS(SS, "sheetData")[0];
  if (!sheetData) return null;
  const filas = new Map<number, Element>();
  for (const row of hijos(sheetData, "row")) filas.set(Number(row.getAttribute("r")) - 1, row);
  return { doc, sheetData, filas, quitoFormulas: false };
}

/** La <row> de esa fila; si no existe, se crea en su sitio copiando el alto de la de arriba. */
function filaXml(h: HojaXml, fila: number): Element {
  const hay = h.filas.get(fila);
  if (hay) return hay;
  const row = h.doc.createElementNS(SS, "row");
  row.setAttribute("r", String(fila + 1));
  const arriba = h.filas.get(fila - 1);
  for (const a of ["ht", "customHeight", "s", "customFormat"]) {
    const v = arriba?.getAttribute(a);
    if (v != null) row.setAttribute(a, v);
  }
  const siguiente = [...h.filas.keys()].filter((k) => k > fila).sort((a, b) => a - b)[0];
  if (siguiente != null) h.sheetData.insertBefore(row, h.filas.get(siguiente)!);
  else h.sheetData.appendChild(row);
  h.filas.set(fila, row);
  return row;
}

/** La <c> de esa celda; si no existe, se crea en su sitio con el estilo de la de arriba. */
function celdaXml(h: HojaXml, fila: number, col: number): Element {
  const row = filaXml(h, fila);
  const ref = refCelda(fila, col);
  const cs = hijos(row, "c");
  const hay = cs.find((c) => c.getAttribute("r") === ref);
  if (hay) return hay;
  const c = h.doc.createElementNS(SS, "c");
  c.setAttribute("r", ref);
  const arriba = h.filas.get(fila - 1);
  const estiloArriba = arriba ? hijos(arriba, "c").find((x) => x.getAttribute("r") === refCelda(fila - 1, col))?.getAttribute("s") : null;
  if (estiloArriba) c.setAttribute("s", estiloArriba);
  const despues = cs.find((x) => partirRef(x.getAttribute("r") ?? "A1").col > col);
  if (despues) row.insertBefore(c, despues);
  else row.appendChild(c);
  // `spans` es una pista de rendimiento: se quita para que no mienta.
  row.removeAttribute("spans");
  return c;
}

function vaciarCelda(h: HojaXml, c: Element) {
  for (const x of Array.from(c.children)) {
    if (x.localName === "f") h.quitoFormulas = true;
    if (x.localName === "v" || x.localName === "f" || x.localName === "is") c.removeChild(x);
  }
  c.removeAttribute("t");
}

function escribirCelda(h: HojaXml, estilos: Estilos | null, fila: number, col: number, valor: Celda) {
  const c = celdaXml(h, fila, col);
  vaciarCelda(h, c);
  if (valor == null || valor === "") return;
  if (typeof valor === "number") {
    const v = h.doc.createElementNS(SS, "v");
    v.textContent = String(valor);
    c.appendChild(v);
    return;
  }
  if (esFechaIso(valor) && estilos) {
    const serial = serialExcel(valor);
    if (serial != null) {
      const s = Number(c.getAttribute("s") ?? "0");
      c.setAttribute("s", String(estilos.conFecha(s)));
      const v = h.doc.createElementNS(SS, "v");
      v.textContent = String(serial);
      c.appendChild(v);
      return;
    }
  }
  c.setAttribute("t", "inlineStr");
  const is = h.doc.createElementNS(SS, "is");
  const t = h.doc.createElementNS(SS, "t");
  preservar(t);
  t.textContent = esFechaIso(valor) ? fechaEspanola(valor) : valor;
  is.appendChild(t);
  c.appendChild(is);
}

/** Estira la Tabla de Excel (ListObject) que empieza en la cabecera hasta `ultimaFila`. */
function estirarTablas(zip: Zip, rutaHoja: string, filaCabecera: number, ultimaFila: number) {
  const carpeta = rutaHoja.split("/").slice(0, -1).join("/");
  const relsRuta = `${carpeta}/_rels/${rutaHoja.split("/").pop()}.rels`;
  const rels = zip[relsRuta];
  if (!rels) return;
  for (const rel of Array.from(xmlDe(rels).getElementsByTagNameNS(PKG_REL, "Relationship"))) {
    if (!/\/table$/.test(rel.getAttribute("Type") ?? "")) continue;
    const ruta = resolverRuta(carpeta, rel.getAttribute("Target") ?? "");
    const u = zip[ruta];
    if (!u) continue;
    const doc = xmlDe(u);
    const tabla = doc.documentElement;
    const ref = tabla.getAttribute("ref");
    if (!ref || !ref.includes(":")) continue;
    const [a, b] = ref.split(":");
    const ini = partirRef(a);
    const fin = partirRef(b);
    if (ini.fila !== filaCabecera || fin.fila >= ultimaFila) continue;
    const nuevo = `${a}:${letraColumna(fin.col)}${ultimaFila + 1}`;
    tabla.setAttribute("ref", nuevo);
    const af = hijos(tabla, "autoFilter")[0];
    if (af) af.setAttribute("ref", nuevo);
    zip[ruta] = aXml(doc);
  }
}

/** Quita calcChain.xml (y sus referencias): Excel lo reconstruye al abrir. */
function quitarCalcChain(zip: Zip) {
  if (!zip["xl/calcChain.xml"]) return;
  delete zip["xl/calcChain.xml"];
  const ct = zip["[Content_Types].xml"];
  if (ct) {
    const d = xmlDe(ct);
    for (const o of Array.from(d.documentElement.children)) {
      if (o.getAttribute("PartName") === "/xl/calcChain.xml") d.documentElement.removeChild(o);
    }
    zip["[Content_Types].xml"] = aXml(d);
  }
  const rels = zip["xl/_rels/workbook.xml.rels"];
  if (rels) {
    const d = xmlDe(rels);
    for (const r of Array.from(d.documentElement.children)) {
      if (/calcChain/.test(r.getAttribute("Type") ?? "")) d.documentElement.removeChild(r);
    }
    zip["xl/_rels/workbook.xml.rels"] = aXml(d);
  }
}

export async function rellenarExcel(original: Uint8Array, hojas: HojaDestino[], relleno: Relleno): Promise<Uint8Array> {
  const zip = await abrirZip(original);
  const estilos = leerEstilos(zip);
  const tocadas = new Map<string, { h: HojaXml; ruta: string; cabecera: number; ultima: number }>();
  const abrir = (nombre: string) => {
    const ruta = rutaDeHoja(zip, nombre);
    if (!ruta || !zip[ruta]) throw new Error(`hoja_no_encontrada:${nombre}`);
    let t = tocadas.get(ruta);
    if (!t) {
      const h = abrirHoja(zip[ruta]);
      if (!h) throw new Error(`hoja_rota:${nombre}`);
      t = { h, ruta, cabecera: -1, ultima: -1 };
      tocadas.set(ruta, t);
    }
    return t;
  };
  const origenDe = (nombre: string) => hojas.find((x) => x.nombre === nombre)?.origen ?? { fila: 0, columna: 0 };

  for (const tabla of relleno.tablas) {
    const t = abrir(tabla.hoja);
    const o = origenDe(tabla.hoja);
    const primera = o.fila + tabla.primeraFila;
    if (tabla.modo === "reemplazar" && tabla.filasExistentes) {
      for (let i = 0; i < tabla.filasExistentes; i++) {
        for (const col of tabla.columnas) {
          const row = t.h.filas.get(primera + i);
          const c = row ? hijos(row, "c").find((x) => x.getAttribute("r") === refCelda(primera + i, o.columna + col)) : undefined;
          if (c) vaciarCelda(t.h, c);
        }
      }
    }
    tabla.filas.forEach((fila, i) => {
      tabla.columnas.forEach((col, j) => escribirCelda(t.h, estilos, primera + i, o.columna + col, fila[j] ?? null));
    });
    t.cabecera = primera - 1;
    t.ultima = Math.max(t.ultima, primera + tabla.filas.length - 1);
  }
  for (const campo of relleno.campos) {
    if (campo.hoja == null || campo.fila == null || campo.columna == null) continue;
    const t = abrir(campo.hoja);
    const o = origenDe(campo.hoja);
    escribirCelda(t.h, estilos, o.fila + campo.fila, o.columna + campo.columna, campo.valor);
  }

  let quitar = false;
  for (const t of tocadas.values()) {
    // La dimensión de la hoja, para que Excel sepa hasta dónde llega.
    const dim = t.h.doc.getElementsByTagNameNS(SS, "dimension")[0];
    const ref = dim?.getAttribute("ref");
    if (dim && ref && t.ultima >= 0) {
      const [a, b] = ref.includes(":") ? ref.split(":") : [ref, ref];
      const fin = partirRef(b);
      if (t.ultima > fin.fila) dim.setAttribute("ref", `${a}:${letraColumna(fin.col)}${t.ultima + 1}`);
    }
    zip[t.ruta] = aXml(t.h.doc);
    if (t.cabecera >= 0 && t.ultima >= 0) estirarTablas(zip, t.ruta, t.cabecera, t.ultima);
    quitar ||= t.h.quitoFormulas;
  }
  if (estilos?.cambiado) zip["xl/styles.xml"] = aXml(estilos.doc);
  if (quitar) quitarCalcChain(zip);
  return cerrarZip(zip);
}

/* ── Word ────────────────────────────────────────────────────────────────── */

const W = "http://schemas.openxmlformats.org/wordprocessingml/2006/main";

/** Pone `texto` en una celda de Word conservando el formato del primer párrafo y del primer trozo. */
function textoEnCelda(doc: Document, tc: Element, texto: string) {
  const ps = hijos(tc, "p");
  const p = ps[0] ?? tc.appendChild(doc.createElementNS(W, "w:p"));
  for (const extra of ps.slice(1)) tc.removeChild(extra);
  const runs = hijos(p, "r");
  const molde = runs[0];
  for (const r of runs) p.removeChild(r);
  // Quita también lo que no sea propiedades del párrafo (enlaces, campos…).
  for (const x of Array.from(p.children)) if (x.localName !== "pPr") p.removeChild(x);
  const r = doc.createElementNS(W, "w:r");
  const rPr = molde ? hijos(molde, "rPr")[0] : undefined;
  if (rPr) r.appendChild(rPr.cloneNode(true));
  texto.split("\n").forEach((linea, i) => {
    if (i > 0) r.appendChild(doc.createElementNS(W, "w:br"));
    const t = doc.createElementNS(W, "w:t");
    preservar(t);
    t.textContent = linea;
    r.appendChild(t);
  });
  p.appendChild(r);
}

/** «Titular: ____» → «Titular: Juan Pérez», conservando el formato de la etiqueta. */
function completarCampo(doc: Document, p: Element, etiqueta: string | undefined, valor: string) {
  const runs = hijos(p, "r");
  let conDosPuntos = -1;
  let acumulado = "";
  runs.forEach((r, i) => {
    const t = hijos(r, "t").map((x) => x.textContent ?? "").join("");
    acumulado += t;
    if (conDosPuntos < 0 && acumulado.includes(":")) conDosPuntos = i;
  });
  if (conDosPuntos < 0) {
    // Sin «:»: se pone todo en un trozo.
    const r = runs[0];
    for (const x of runs) p.removeChild(x);
    const nuevo = doc.createElementNS(W, "w:r");
    const rPr = r ? hijos(r, "rPr")[0] : undefined;
    if (rPr) nuevo.appendChild(rPr.cloneNode(true));
    const t = doc.createElementNS(W, "w:t");
    preservar(t);
    t.textContent = `${etiqueta ? `${etiqueta}: ` : ""}${valor}`;
    nuevo.appendChild(t);
    p.appendChild(nuevo);
    return;
  }
  // El trozo con «:» se corta justo después de los dos puntos; lo de detrás (las rayas) fuera.
  const rDos = runs[conDosPuntos];
  const ts = hijos(rDos, "t");
  let visto = false;
  for (const t of ts) {
    if (visto) {
      rDos.removeChild(t);
      continue;
    }
    const s = t.textContent ?? "";
    const i = s.indexOf(":");
    if (i >= 0) {
      t.textContent = s.slice(0, i + 1);
      visto = true;
    }
  }
  const molde = runs[conDosPuntos + 1] ?? rDos;
  for (const r of runs.slice(conDosPuntos + 1)) p.removeChild(r);
  const nuevo = doc.createElementNS(W, "w:r");
  const rPr = hijos(molde, "rPr")[0];
  if (rPr) {
    const copia = rPr.cloneNode(true) as Element;
    // El valor no va en negrita aunque la etiqueta lo esté.
    if (molde === rDos) for (const b of hijos(copia, "b")) copia.removeChild(b);
    nuevo.appendChild(copia);
  }
  const t = doc.createElementNS(W, "w:t");
  preservar(t);
  t.textContent = ` ${valor}`;
  nuevo.appendChild(t);
  p.appendChild(nuevo);
}

export async function rellenarWord(original: Uint8Array, hojas: HojaDestino[], relleno: Relleno): Promise<Uint8Array> {
  const zip = await abrirZip(original);
  const u = zip["word/document.xml"];
  if (!u) throw new Error("docx_sin_documento");
  const doc = xmlDe(u);
  const body = doc.getElementsByTagNameNS(W, "body")[0];
  if (!body) throw new Error("docx_sin_cuerpo");
  const delCuerpo = Array.from(body.children);
  const tablas = delCuerpo.filter((x) => x.localName === "tbl");
  const parrafos = delCuerpo.filter((x) => x.localName === "p");

  for (const r of relleno.tablas) {
    const indice = hojas.find((h) => h.nombre === r.hoja)?.indice;
    const tbl = indice != null ? tablas[indice] : undefined;
    if (!tbl) throw new Error(`tabla_no_encontrada:${r.hoja}`);
    let filas = hijos(tbl, "tr");
    const molde = filas[r.primeraFila] ?? filas[filas.length - 1];
    if (!molde) continue;
    const moldeLimpio = molde.cloneNode(true) as Element;
    // «reemplazar»: las filas de datos viejas se quitan (se deja la primera como molde).
    if (r.modo === "reemplazar" && r.filasExistentes) {
      for (const tr of filas.slice(r.primeraFila + 1, r.primeraFila + r.filasExistentes)) tbl.removeChild(tr);
      filas = hijos(tbl, "tr");
    }
    r.filas.forEach((valores, i) => {
      let tr = filas[r.primeraFila + i];
      /* Una fila NUEVA (clonada del molde) sale limpia: se vacían también las
         columnas que no se rellenan. En una que ya estaba, solo se tocan las
         columnas elegidas; con «reemplazar», la primera (la que queda de las viejas)
         cuenta como nueva. */
      let nueva = r.modo === "reemplazar" && i === 0;
      if (!tr) {
        tr = moldeLimpio.cloneNode(true) as Element;
        tbl.appendChild(tr);
        filas = hijos(tbl, "tr");
        nueva = true;
      }
      const tcs = hijos(tr, "tc");
      if (nueva) tcs.forEach((tc, k) => { if (!r.columnas.includes(k)) textoEnCelda(doc, tc, ""); });
      r.columnas.forEach((col, j) => {
        const tc = tcs[col];
        if (tc) textoEnCelda(doc, tc, textoDeCelda(valores[j] ?? null));
      });
    });
  }
  for (const c of relleno.campos) {
    if (c.parrafo == null) continue;
    const p = parrafos[c.parrafo];
    if (p) completarCampo(doc, p, c.etiqueta, c.valor);
  }
  zip["word/document.xml"] = aXml(doc);
  return cerrarZip(zip);
}

/* ── CSV ─────────────────────────────────────────────────────────────────── */

/** El CSV con las filas escritas en su sitio (mismo separador, coma decimal si era «;»). */
export function rellenarCsv(sep: string, celdas: Celda[][], relleno: Relleno): string {
  const matriz = celdas.map((f) => f.map((c) => c));
  for (const t of relleno.tablas) {
    if (t.modo === "reemplazar" && t.filasExistentes) {
      for (let i = 0; i < t.filasExistentes; i++) for (const c of t.columnas) if (matriz[t.primeraFila + i]) matriz[t.primeraFila + i][c] = null;
    }
    t.filas.forEach((f, i) => {
      const r = t.primeraFila + i;
      while (matriz.length <= r) matriz.push([]);
      t.columnas.forEach((c, j) => {
        while (matriz[r].length <= c) matriz[r].push(null);
        matriz[r][c] = f[j] ?? null;
      });
    });
  }
  const comaDecimal = sep !== ",";
  const celdaCsv = (v: Celda) => {
    let s = v == null ? "" : typeof v === "number" ? (comaDecimal ? String(v).replace(".", ",") : String(v)) : esFechaIso(v) ? fechaEspanola(v) : v;
    if (s.includes(sep) || s.includes('"') || s.includes("\n")) s = `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  return matriz.map((f) => f.map(celdaCsv).join(sep)).join("\r\n") + "\r\n";
}

export const MIME: Record<"xlsx" | "docx" | "csv", string> = {
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  csv: "text/csv",
};

/** «Plantilla cooperativa.xlsx» → «Plantilla cooperativa (rellena).xlsx». */
export function nombreRelleno(nombre: string): string {
  const i = nombre.lastIndexOf(".");
  return i > 0 ? `${nombre.slice(0, i)} (rellena)${nombre.slice(i)}` : `${nombre} (rellena)`;
}
